import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import loginImage from '../../assets/images/homepage.png';
import { useGoogleLogin } from '@react-oauth/google';
import { loginWithGoogle, loginWithMojoAuth } from '../../services/authService';
import { validateUser } from '../../services/userProfileService';
import { useNavigate } from 'react-router-dom';
import MojoAuth from 'mojoauth-web-sdk';
import './LoginPopup.css';
import SubscriptionExpiredDialog from '../Subscription/SubscriptionExpiredDialog';

function LoginPopup({ open, onClose }) {

  const INTERNAL_LOGIN_TYPE_ID = 1;
  const navigate = useNavigate();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isLoadingMojo, setIsLoadingMojo] = useState(false);
  const [subscriptionExpiredOpen, setSubscriptionExpiredOpen] = useState(false);
  const [subscriptionExpiryDate, setSubscriptionExpiryDate] = useState(null);

  const formatExpiryDate = (raw) => {
    if (raw == null || raw === '') return null;
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const pickExpiryRawFromValidateResponse = (response) =>
    response?.subscription_end_at ??
    response?.end_at ??
    response?.subscription?.end_at;

  const navigateAfterAuth = (subscriptionStatus) => {
    const pendingPlan = sessionStorage.getItem('pendingPlan');
    if (pendingPlan) {
      // Stay on subscription page after login; SubscriptionPlans reads pendingPlan and clears it.
      navigate('/subscribe');
      return;
    }
    if (
      subscriptionStatus === 'active' ||
      subscriptionStatus === 'completed'
    ) {
      navigate('/libraryManager');
    } else {
      navigate('/subscribe');
    }
  };

  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        const { data } = await loginWithGoogle(codeResponse.code);

          localStorage.setItem('access_token', data.id_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem("auth_provider", "google");
          localStorage.setItem('employeeName', data.name);

          const [first_name, last_name = ''] = data.name.trim().split(' ');

          const googleUser = {
            email: data.email,
            login_type_id: INTERNAL_LOGIN_TYPE_ID,
            first_name,
            last_name,
          };

          const response = await validateUser(googleUser);
          if (response.success === true) {
            localStorage.setItem('user_id', response.user_id);
            onClose();
            // console.log("response", response);
            if (response.subscription_expired) {
              setSubscriptionExpiryDate(
                formatExpiryDate(pickExpiryRawFromValidateResponse(response))
              );
              setSubscriptionExpiredOpen(true);
              return;
            }
            navigateAfterAuth(data.subscriptionStatus);
          } else {
            navigate('/');
          }
      } catch (err) {
        console.error('Error:', err);
      }
    },
    onError: () => console.log('Login Failed'),
    scope: 'profile email',
    ux_mode: 'popup',
    flow: 'auth-code',
    access_type: 'offline',
    prompt: 'consent',
  });

  useEffect(() => {
    if (showEmailForm) {
      setIsLoadingMojo(true);
      const mojoauth = new MojoAuth(process.env.REACT_APP_MOJOAUTH_API_KEY, {
        language: "en",
        redirect_url: process.env.REACT_APP_MOJOAUTH_REDIRECT_URL,
        source: [{ type: "email", feature: "otp" }],
      });

      mojoauth.signIn().then((payload) => {
        handleMojoLogin(payload);
      });

      // Check if the form element exists
      const checkInterval = setInterval(() => {
        const mojoForm = document.getElementById('mojoauth-passwordless-form');
        if (mojoForm && mojoForm.children.length > 0) {
          setIsLoadingMojo(false);
          clearInterval(checkInterval);
        }
      }, 100);

      return () => {
        clearInterval(checkInterval);
      };
    }
  }, [showEmailForm]);

  const handleMojoLogin = async (payload) => {
    if (!payload?.user?.email) return;

    // Show loading when processing OTP
    setIsLoadingMojo(true);

    const access_token = payload.oauth.access_token;
    const userEmail = payload.user.email;

    try {
      const { data } = await loginWithMojoAuth(access_token);

        // Store JWT tokens
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('auth_provider', 'mojoauth');
        localStorage.setItem('employeeName', `${data.first_name} ${data.last_name}`);

        const mojoUser = {
          email: userEmail,
          login_type_id: INTERNAL_LOGIN_TYPE_ID,
          first_name: data.first_name,
          last_name: data.last_name,
        };

        // call validateUser same as Google flow
        const response = await validateUser(mojoUser);
        if (response.success === true) {
          localStorage.setItem('user_id', response.user_id);
          onClose();
          setIsLoadingMojo(false);
          if (response.subscription_expired) {
            setSubscriptionExpiryDate(
              formatExpiryDate(pickExpiryRawFromValidateResponse(response))
            );
            setSubscriptionExpiredOpen(true);
            return;
          }
          navigateAfterAuth(data.subscriptionStatus);
        } else {
          navigate('/');
        }
    } catch (err) {
      console.error('MojoAuth login error:', err);
      setIsLoadingMojo(false);
    }
  };

  const handleRenewSubscription = () => {
    setSubscriptionExpiredOpen(false);
    setSubscriptionExpiryDate(null);
    navigate('/subscribe');
  };

  const handleClose = () => {
    setShowEmailForm(false);
    setIsLoadingMojo(false);
    onClose();
  };

  const handleBackToLogin = () => {
    setShowEmailForm(false);
    setIsLoadingMojo(false);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '90%', sm: '90%' ,md:'85%', lg:"60%"},
          height: { xs: '400px', sm: '600px' },
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <IconButton size='small'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 10,
          backgroundColor: 'rgba(0,0,0,0.05)',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.15)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {showEmailForm && (
        <IconButton
          size='small'
          onClick={handleBackToLogin}
          className='login-back-button'
        >
          <ArrowBackIcon />
        </IconButton>
      )}

      <Grid container sx={{ height: '100%', display: "flex", flexDirection: "column"}}>
        <Grid size={{ xs:12 ,sm: 6}}
          sx={{
            p: { xs: 3, sm: 6 },
          }}
          className='login-fields'
        >
          {showEmailForm ? (
            // <Box>
            //   <div id="mojoauth-passwordless-form" />
            // </Box>
            <Box className='email-form-container'>
              {isLoadingMojo && (
                <Box className='mojo-loader-overlay'>
                  <CircularProgress size={40} />
                  <Typography variant="body2" className='mojo-loader-text'>
                    {isLoadingMojo && document.getElementById('mojoauth-passwordless-form')?.children.length > 0
                      ? 'Verifying...'
                      : 'Loading...'}
                  </Typography>
                </Box>
              )}
              <div 
                id="mojoauth-passwordless-form" 
                style={{ opacity: isLoadingMojo ? 0.3 : 1, pointerEvents: isLoadingMojo ? 'none' : 'auto' }}
              />
            </Box>
          ) : (
            <Box>
              <Typography variant="h1" sx={{ flexGrow: 1, textAlign:"center", mb: 2 }} className="sniglet-font-text">PHYtool</Typography>
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  textAlign: "left !important"
                }}
              >
                Log in or sign up in seconds
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 3,
                  fontSize: '14px',
                  lineHeight: '1.6',
                  textAlign:"left !important"
                }}
              >
                Use your email to continue
              </Typography>

                <Button
                  variant="outlined"
                  onClick={login}
                  startIcon={
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      width="20"
                    />
                  }
                  sx={{
                    mb: 2,
                    width: {xs:"100%",sm:"90%"},
                    textTransform: 'none',
                    fontSize: '14px',
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    color: "black !important",
                    borderColor: "grey !important"
                  }}
                >
                  Continue with Google
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => setShowEmailForm(true)}
                  startIcon={<MailOutlineIcon />}
                  sx={{
                    mb: 2,
                    width: {xs:"100%",sm:"90%"},
                    textTransform: 'none',
                    fontSize: '14px',
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    color: "black !important",
                    borderColor: "grey !important"
                  }}
                >
                  Continue with Email
                </Button>
                <br/>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 3, fontSize: '14px', lineHeight: '1.5' }}
              >
                By continuing, you agree to our{' '}
                <span style={{ color: '#1976d2', cursor: 'pointer' }}>Terms of Use</span> and{' '}
                <span style={{ color: '#1976d2', cursor: 'pointer' }}>Privacy Policy</span>.
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid size={{ xs:12 ,sm: 6}}
          sx={{
            height: '100%',
          }}
        >
          <Box
            component="img"
            src={loginImage}
            alt="Login Visual"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Grid>
      </Grid>
    </Dialog>

    <SubscriptionExpiredDialog
      open={subscriptionExpiredOpen}
      onRenew={handleRenewSubscription}
      expiryDateFormatted={subscriptionExpiryDate}
    />
  </>
  );
}

export default LoginPopup;
